import React, { useState } from 'react';
import {
  Title,
  Text,
  Group,
  Stack,
  Card,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Tooltip,
  Badge,
  Chip,
  Checkbox,
  Loader,
  Alert,
  Avatar,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import { LoadFailed } from '../../components/ui/LoadFailed';
import { IconSearch, IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { CompetencyCreateModal } from '../../components/competency/CompetencyCreateModal';
import { modals } from '@mantine/modals';
import { getErrorMessage } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { Competency } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  useCompetencies,
  useCompetencyMaps,
  useDeleteCompetency,
  useUpdateCompetency,
} from '../../hooks/useApi';
import { useDevice } from '../../hooks/useDevice';
import { usePermission } from '../../hooks/usePermission';

export const Competencies: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMapFilter, setSelectedMapFilter] = useState<string>('all');
  const [mentorsFilter, setMentorsFilter] = useState<string>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDomain, setBulkDomain] = useState<string>('technical');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCompetency, setEditCompetency] = useState<Competency | null>(null);
  const isAdmin = usePermission('maps.manage');
  const deviceType = useDevice();

  // Зоны ответственности (домены) компетенций и их цвета.
  const DOMAIN_KEYS = ['technical', 'qa', 'devops', 'product', 'soft'];
  const DOMAIN_COLORS: Record<string, string> = {
    technical: 'blue',
    qa: 'grape',
    devops: 'orange',
    product: 'teal',
    soft: 'pink',
  };

  // null/undefined-домен трактуется бэком как technical (нужно проставить явно).
  const renderDomainBadge = (competency: Competency) => {
    const isSet = !!competency.domain;
    const domain = competency.domain || 'technical';
    return (
      <Tooltip label={isSet ? t('competencyMaps.domainHint') : t('competencies.domainUnsetHint')}>
        <Badge
          size="sm"
          variant={isSet ? 'light' : 'outline'}
          color={isSet ? DOMAIN_COLORS[domain] : 'gray'}
        >
          {t(`competencyMaps.domains.${domain}`)}
          {!isSet && ` · ${t('competencies.domainUnset')}`}
        </Badge>
      </Tooltip>
    );
  };

  // Получаем все компетенции с информацией о картах
  const { data: competencies = [], isLoading, error } = useCompetencies();

  // Получаем все карты компетенций для фильтра
  const { data: allMaps = [] } = useCompetencyMaps();

  // Мутация для удаления компетенции
  const deleteCompetencyMutation = useDeleteCompetency();
  const updateCompetencyMutation = useUpdateCompetency();

  // Собираем всех уникальных менторов из всех компетенций
  const allMentors = React.useMemo(() => {
    const mentorMap = new Map<string, string>();
    competencies.forEach((competency) => {
      if (competency.mentors) {
        competency.mentors.forEach((mentor) => {
          if (typeof mentor === 'object' && mentor._id && mentor.name) {
            mentorMap.set(mentor._id, mentor.name);
          } else if (typeof mentor === 'string') {
            // Если это строка, то у нас нет имени, пропустим
            // mentorMap.set(mentor, mentor);
          }
        });
      }
    });
    return Array.from(mentorMap.entries()).map(([id, name]) => ({ id, name }));
  }, [competencies]);

  // Фильтрация компетенций по поисковому запросу, карте и менторам
  const filteredCompetencies = competencies.filter((competency) => {
    // Фильтр по поисковому запросу
    const matchesSearch = searchInObject(competency, searchQuery, [
      'title',
      'description',
      'mentors',
    ]);

    // Фильтр по карте компетенций
    const matchesMap =
      selectedMapFilter === 'all' ||
      (competency.maps && competency.maps.some((map) => map._id === selectedMapFilter));

    // Фильтр по менторам
    let matchesMentors = true;
    if (mentorsFilter === 'no-mentors') {
      matchesMentors = !competency.mentors || competency.mentors.length === 0;
    } else if (mentorsFilter !== 'all') {
      // Фильтр по конкретному ментору
      matchesMentors =
        competency.mentors?.some((mentor) => {
          const mentorId = typeof mentor === 'object' ? mentor._id : mentor;
          return mentorId === mentorsFilter;
        }) || false;
    }

    // Фильтр по домену (зоне ответственности)
    let matchesDomain = true;
    if (domainFilter === 'unset') {
      matchesDomain = !competency.domain;
    } else if (domainFilter !== 'all') {
      matchesDomain = (competency.domain || 'technical') === domainFilter;
    }

    return matchesSearch && matchesMap && matchesMentors && matchesDomain;
  });

  const handleEdit = (competency: Competency) => {
    setEditCompetency(competency);
    setShowCreateModal(true);
  };

  const handleDelete = (competency: Competency) => {
    modals.openConfirmModal({
      title: t('competencies.deleteConfirmTitle', { name: competency.title }),
      children: (
        <Text size="sm">{t('competencies.deleteConfirmText', { name: competency.title })}</Text>
      ),
      labels: { confirm: t('competencies.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteCompetencyMutation.mutateAsync(competency._id);
          showToast({
            title: t('competencies.deleted'),
            message: t('competencies.deleteConfirmText', { name: competency.title }),
            color: 'teal',
          });
        } catch (error) {
          const message = getErrorMessage(error, t('competencies.deleteError'));
          showToast({ title: t('common.error'), message, color: 'red' });
        }
      },
    });
  };

  const handleCreate = () => {
    setEditCompetency(null);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditCompetency(null);
  };

  // Массовый выбор для bulk-простановки зоны ответственности.
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
    filteredCompetencies.length > 0 && filteredCompetencies.every((c) => selectedIds.has(c._id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (filteredCompetencies.every((c) => next.has(c._id))) {
        filteredCompetencies.forEach((c) => next.delete(c._id));
      } else {
        filteredCompetencies.forEach((c) => next.add(c._id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDomain = async () => {
    // Применяем только к видимым (отфильтрованным) — чтобы скрытые фильтром не задеть.
    const visible = new Set(filteredCompetencies.map((c) => c._id));
    const ids = Array.from(selectedIds).filter((id) => visible.has(id));
    if (ids.length === 0) return;

    const results = await Promise.allSettled(
      ids.map((id) =>
        updateCompetencyMutation.mutateAsync({ _id: id, data: { domain: bulkDomain } }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;

    if (failed === 0) {
      showToast({
        title: t('competencies.bulkDomainDone', { count: ok }),
        message: '',
        color: 'teal',
      });
    } else {
      showToast({
        title: t('competencies.bulkDomainPartial', { ok, failed }),
        message: '',
        color: ok === 0 ? 'red' : 'yellow',
      });
    }
    clearSelection();
  };

  // Функция поиска в объекте
  function searchInObject(obj: Competency, query: string, fields: (keyof Competency)[]): boolean {
    if (!query) return true;
    const lowerQuery = query.toLowerCase();

    return fields.some((field) => {
      const value = obj[field];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowerQuery);
      }
      if (Array.isArray(value)) {
        return value.some((item: unknown) => {
          if (typeof item === 'string') {
            return item.toLowerCase().includes(lowerQuery);
          }
          if (typeof item === 'object' && item !== null && 'name' in item) {
            return String((item as { name: string }).name)
              .toLowerCase()
              .includes(lowerQuery);
          }
          return false;
        });
      }
      return false;
    });
  }

  if (!isAdmin) {
    return (
      <PageContainer>
        <Alert color="red" title={t('common.error')}>
          {t('common.accessDenied')}
        </Alert>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <LoadFailed message={t('errors.loadFailed')} hasRetry />
      </PageContainer>
    );
  }

  const rows = filteredCompetencies.map((competency) => (
    <Table.Tr key={competency._id}>
      <Table.Td>
        <Checkbox
          checked={selectedIds.has(competency._id)}
          onChange={() => toggleSelect(competency._id)}
          aria-label={competency.title}
        />
      </Table.Td>
      <Table.Td>
        <Stack gap={4}>
          <Text fw={500} size="sm">
            {competency.title}
          </Text>
          {competency.description && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {competency.description}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Stack gap={4}>
          {competency.maps && competency.maps.length > 0 ? (
            competency.maps.map((map) => (
              <Chip
                key={map._id}
                size="xs"
                variant="light"
                color="gray"
                checked={false}
                style={{ pointerEvents: 'none', cursor: 'default' }}
              >
                {map.profession}
              </Chip>
            ))
          ) : (
            <Text size="xs" c="dimmed">
              {t('competencies.noMaps')}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>{renderDomainBadge(competency)}</Table.Td>
      <Table.Td>
        <Group gap={4}>
          {competency.mentors && competency.mentors.length > 0 ? (
            <Group gap={4} wrap="nowrap">
              {competency.mentors.slice(0, 3).map((mentor, index) => {
                const mentorName = typeof mentor === 'string' ? mentor : mentor.name;
                const mentorAvatar = typeof mentor === 'string' ? undefined : mentor.avatar;
                const mentorEmail = typeof mentor === 'string' ? mentor : mentor.email;

                return (
                  <Tooltip
                    key={index}
                    label={`${mentorName}${mentorEmail ? ` (${mentorEmail})` : ''}`}
                  >
                    <Avatar src={mentorAvatar} alt={mentorName} size="sm" radius="xl">
                      {mentorName.charAt(0).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                );
              })}
              {competency.mentors.length > 3 && (
                <Tooltip label={`${competency.mentors.length - 3} ${t('common.more')}`}>
                  <Avatar size="xs" radius="xl">
                    +{competency.mentors.length - 3}
                  </Avatar>
                </Tooltip>
              )}
            </Group>
          ) : (
            <Text size="xs" c="dimmed">
              {t('competencies.noMentors')}
            </Text>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap={4} justify="center">
          <Tooltip label={t('competencies.edit')}>
            <ActionIcon
              size="md"
              color="teal"
              variant="light"
              onClick={() => handleEdit(competency)}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('competencies.delete')}>
            <ActionIcon
              size="md"
              color="red"
              variant="light"
              onClick={() => handleDelete(competency)}
              loading={deleteCompetencyMutation.isPending}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PageContainer>
      <Group gap="sm" align="center" mb={{ base: 'md', sm: 'xl' }} wrap="wrap">
        <Title order={2} size="h3">
          {t('competencies.title')}
        </Title>
        <Badge color="teal" variant="light" size="sm">
          {filteredCompetencies.length}
          {deviceType !== 'mobile' &&
            ` ${t('competencies.countWord', { count: filteredCompetencies.length })}`}
        </Badge>
        {deviceType === 'mobile' && (
          <ActionIcon onClick={handleCreate} variant="light" size="md" ml="auto">
            <IconPlus size={14} />
          </ActionIcon>
        )}
      </Group>

      <Card withBorder radius="md" p={{ base: 'sm', sm: 'md' }} mb="md">
        <Stack gap="md" mb="md">
          {/* Фильтры */}
          <Group justify="space-between" gap="sm">
            <Group gap="sm" style={{ flex: 1, minWidth: 280 }} wrap="nowrap">
              <TextInput
                placeholder={t('common.search')}
                leftSection={<IconSearch size={14} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ flex: 1, minWidth: 200 }}
                size="sm"
              />
              <Select
                placeholder={t('competencies.filterByMap')}
                value={selectedMapFilter}
                onChange={(value) => setSelectedMapFilter(value || 'all')}
                data={[
                  { value: 'all', label: t('competencies.allMaps') },
                  ...allMaps.map((map) => ({
                    value: map._id,
                    label: map.name,
                  })),
                ]}
                clearable={false}
                size="sm"
              />
              <Select
                placeholder={t('competencies.filterByMentors')}
                value={mentorsFilter}
                onChange={(value) => setMentorsFilter(value || 'all')}
                data={[
                  { value: 'all', label: t('competencies.allMentors') },
                  { value: 'no-mentors', label: t('competencies.noMentors') },
                  ...allMentors.map((mentor) => ({
                    value: mentor.id,
                    label: mentor.name,
                  })),
                ]}
                clearable={false}
                size="sm"
              />
              <Select
                placeholder={t('competencies.filterByDomain')}
                value={domainFilter}
                onChange={(value) => setDomainFilter(value || 'all')}
                data={[
                  { value: 'all', label: t('competencies.allDomains') },
                  { value: 'unset', label: t('competencies.domainUnset') },
                  ...DOMAIN_KEYS.map((d) => ({
                    value: d,
                    label: t(`competencyMaps.domains.${d}`),
                  })),
                ]}
                clearable={false}
                size="sm"
              />
            </Group>
            {deviceType !== 'mobile' && (
              <Button
                leftSection={<IconPlus size={14} />}
                onClick={handleCreate}
                variant="light"
                size="sm"
              >
                {t('competencies.addCompetency')}
              </Button>
            )}
          </Group>
        </Stack>

        {selectedIds.size > 0 && (
          <Group gap="sm" mb="md" wrap="wrap" align="flex-end">
            <Text size="sm" fw={500}>
              {t('competencies.selectedCount', { count: selectedIds.size })}
            </Text>
            <Select
              label={t('competencyMaps.domain')}
              data={DOMAIN_KEYS.map((d) => ({
                value: d,
                label: t(`competencyMaps.domains.${d}`),
              }))}
              value={bulkDomain}
              onChange={(v) => setBulkDomain(v || 'technical')}
              size="sm"
              w={220}
            />
            <Button
              size="sm"
              onClick={handleBulkDomain}
              loading={updateCompetencyMutation.isPending}
            >
              {t('competencies.applyDomain')}
            </Button>
            <Button size="sm" variant="subtle" color="gray" onClick={clearSelection}>
              {t('common.cancel')}
            </Button>
          </Group>
        )}

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : filteredCompetencies.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">
            {/* Пусто из-за фильтров (поиск, карта, ментор) — это «ничего не найдено», а не «компетенций нет». */}
            {competencies.length > 0
              ? t('common.noSearchResults')
              : t('competencies.noCompetencies')}
          </Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover miw={600}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 40 }}>
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={selectedIds.size > 0 && !allFilteredSelected}
                      onChange={toggleSelectAll}
                      aria-label={t('competencies.selectAll')}
                    />
                  </Table.Th>
                  <Table.Th style={{ minWidth: 200 }}>{t('competencies.name')}</Table.Th>
                  <Table.Th style={{ minWidth: 150 }}>{t('competencies.maps')}</Table.Th>
                  <Table.Th style={{ minWidth: 120 }}>{t('competencyMaps.domain')}</Table.Th>
                  <Table.Th style={{ minWidth: 120 }}>{t('competencies.mentors')}</Table.Th>
                  <Table.Th style={{ minWidth: 80, textAlign: 'center' }}>
                    {t('common.actions')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </div>
        )}
      </Card>

      <CompetencyCreateModal
        opened={showCreateModal}
        onClose={handleCloseModal}
        swimlanes={[]}
        grades={[]}
        editingCompetency={editCompetency}
        hideSwimlaneAndGrade={true}
        hideModeSelection={true}
        hideRequiredField={true}
      />
    </PageContainer>
  );
};
